import React, { useEffect, useState } from "react";
import { CreditCard, RefreshCw, Loader2, BellRing, CheckCircle2 } from "lucide-react";
import api from "../../lib/api";
import { PageTitle, Card, Badge, Loading, fmtDate } from "./ui";
import { useAuth } from "../../context/AuthContext";

export default function Dues() {
  const { user } = useAuth();
  const [dues, setDues] = useState(undefined);
  const [paying, setPaying] = useState(false);
  const [adminDues, setAdminDues] = useState(null);
  const [remind, setRemind] = useState({ state: "idle", result: null });

  useEffect(() => {
    api.get("/portal/dues").then((r) => setDues(r.data)).catch(() => setDues(null));
  }, []);

  useEffect(() => {
    if (user?.is_admin) {
      api.get("/admin/dues").then((r) => setAdminDues(r.data)).catch(() => setAdminDues([]));
    }
  }, [user]);

  const sendReminders = async () => {
    setRemind({ state: "sending", result: null });
    try {
      const { data } = await api.post("/admin/dues/remind");
      setRemind({ state: "done", result: data });
    } catch {
      setRemind({ state: "error", result: null });
    }
  };

  const payNow = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/dues/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch {
      setPaying(false);
    }
  };

  if (dues === undefined) return <Loading />;
  const unpaid = (adminDues || []).filter((d) => d.status !== "Paid");

  return (
    <div data-testid="dues-page">
      <PageTitle eyebrow="Subscriptions" title="Dues & Payments" sub="Your annual subscription and payment history." />

      {!dues ? (
        <Card><p className="font-sans text-ink/65">No dues record found for your account.</p></Card>
      ) : (
        <div className="grid gap-6 lg:grid-cols-3">
          <Card className="lg:col-span-1">
            <div className="flex items-center gap-2 text-gold-deep"><CreditCard size={18} /><span className="font-sans text-xs font-semibold uppercase tracking-widest">Current Balance</span></div>
            <p className="mt-5 font-display text-5xl font-semibold text-sapphire-deep">S${dues.amount}</p>
            <p className="font-sans text-sm text-ink/55">Financial Year {dues.year}</p>
            <div className="mt-4"><Badge>{dues.status}</Badge></div>
            <button
              disabled={dues.status === "Paid" || paying}
              onClick={payNow}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gold py-3 font-sans text-sm font-semibold uppercase tracking-widest text-sapphire-deep transition hover:bg-gold-light disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="dues-pay"
            >
              {paying ? <Loader2 size={15} className="animate-spin" /> : null}
              {dues.status === "Paid" ? "Fully Paid" : paying ? "Redirecting…" : "Pay Now"}
            </button>
            <label className="mt-4 flex items-center justify-center gap-2 font-sans text-xs text-ink/55">
              <RefreshCw size={13} className="text-gold-deep" /> Auto-renewal available at next cycle
            </label>
          </Card>

          <Card className="lg:col-span-2">
            <h3 className="font-display text-xl font-semibold text-sapphire-deep">Payment History</h3>
            <div className="mt-5 divide-y divide-sapphire-deep/10">
              {(dues.history || []).map((h, i) => (
                <div key={i} className="flex items-center justify-between py-4">
                  <div>
                    <p className="font-display text-lg text-sapphire-deep">Financial Year {h.year}</p>
                    <p className="font-sans text-sm text-ink/50">Annual subscription</p>
                  </div>
                  <div className="text-right">
                    <p className="font-sans text-lg text-sapphire-deep">S${h.amount}</p>
                    <Badge>{h.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {user?.is_admin && (
        <div className="mt-10" data-testid="admin-dues-panel">
          <div className="mb-4 flex items-center gap-2">
            <BellRing size={18} className="text-gold-deep" />
            <h2 className="font-display text-2xl font-semibold text-sapphire-deep">Treasurer — Dues Reminders</h2>
          </div>
          <Card>
            <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="font-sans text-sm text-ink/70">
                  {adminDues === null
                    ? "Loading members…"
                    : `${unpaid.length} of ${adminDues.length} members have outstanding dues.`}
                </p>
                <p className="mt-1 font-sans text-xs text-ink/50">
                  Sends a warm reminder email to every member whose dues are not yet paid.
                </p>
              </div>
              <button
                onClick={sendReminders}
                disabled={remind.state === "sending" || unpaid.length === 0}
                className="flex items-center justify-center gap-2 rounded-full bg-sapphire-deep px-6 py-3 font-sans text-sm font-semibold uppercase tracking-widest text-parchment transition hover:bg-sapphire-mid disabled:opacity-40"
                data-testid="dues-remind-btn"
              >
                {remind.state === "sending" ? <Loader2 size={15} className="animate-spin" /> : <BellRing size={15} />}
                {remind.state === "sending" ? "Sending…" : "Send Dues Reminders"}
              </button>
            </div>

            {remind.state === "done" && remind.result && (
              <div className="mt-5 flex items-start gap-2 rounded-xl bg-emerald/8 p-4 font-sans text-sm text-emerald" data-testid="dues-remind-result">
                <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
                <span>
                  Sent {remind.result.sent} of {remind.result.attempted} reminder(s).
                  {!remind.result.email_configured && " (Email is not configured, so none were actually delivered.)"}
                </span>
              </div>
            )}
            {remind.state === "error" && (
              <p className="mt-4 font-sans text-sm text-garnet">Could not send reminders. Please try again.</p>
            )}

            {adminDues && adminDues.length > 0 && (
              <div className="mt-6 divide-y divide-sapphire-deep/10">
                {adminDues.map((d) => (
                  <div key={d.email} className="flex items-center justify-between py-3">
                    <div>
                      <p className="font-display text-lg text-sapphire-deep">{d.name}</p>
                      <p className="font-sans text-xs text-ink/50">{d.rank} · FY {d.year}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-sans text-sm text-ink/60">S${d.amount}</span>
                      <Badge>{d.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
